import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const navLinkClass = (active: boolean) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      active
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-background-200"
          : "bg-background-50 border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-play-fill text-background-50 text-xl w-6 h-6 flex items-center justify-center"></i>
            </span>
            <span className="text-xl font-heading font-bold tracking-tight hidden sm:block">
              <span className="text-primary-500">西瓜</span>
              <span className="text-foreground-900">视频</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center ml-4">
            <Link to="/" className={navLinkClass(location.pathname === "/")}>
              首页
            </Link>
            <div
              className="relative"
              ref={catRef}
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-2 w-[420px] z-50">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 rounded-md bg-background-100 text-primary-500 flex items-center justify-center shrink-0">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/about-us"
              className={navLinkClass(location.pathname === "/about-us")}
            >
              关于我们
            </Link>
          </nav>

          <form
            onSubmit={handleSearch}
            className="hidden md:flex items-center flex-1 max-w-sm ml-auto"
          >
            <div className="relative w-full">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频、影视、综艺…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
              />
            </div>
          </form>

          <div className="flex items-center ml-auto md:ml-2">
            <Link
              to="/search"
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="菜单"
            >
              <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative w-full">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索视频、影视、综艺…"
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 focus:outline-none focus:border-primary-400"
                />
              </div>
            </form>
            <div className="flex flex-col">
              <Link to="/" className="py-2.5 text-sm font-medium text-foreground-800">
                首页
              </Link>
              <Link to="/about-us" className="py-2.5 text-sm font-medium text-foreground-800">
                关于我们
              </Link>
            </div>
            <div className="mt-2 pt-3 border-t border-background-200">
              <p className="text-xs text-foreground-500 mb-2">视频分类</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 bg-background-100"
                  >
                    <i className={`${cat.icon} text-primary-500`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}